// RequireAuth.tsx — route guard.
// - MSAL configured: full Entra flow (guard loaded lazily with the MSAL chunk).
// - MSAL absent + VITE_USE_REAL_API: dev JWT login (redirect to /login if no token).
// - Neither: pass-through (mocks).

import { Suspense, lazy, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authEnabled } from './msalConfig';
import { useRealApi } from '@/api/paths';
import { getAccessToken } from './devAuth';

const LazyMsalGuard = lazy(() => import('./msalRuntime').then((m) => ({ default: m.MsalGuard })));

function DevJwtGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAccessToken();

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }
  }, [token, navigate, location.pathname]);

  if (!token) return null;
  return <>{children}</>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  if (authEnabled) {
    return (
      <Suspense fallback={null}>
        <LazyMsalGuard>{children}</LazyMsalGuard>
      </Suspense>
    );
  }
  if (useRealApi) return <DevJwtGuard>{children}</DevJwtGuard>;
  return <>{children}</>;
}
